import type React from "react";
import { ActionIcon, Divider, Group, Indicator, Popover, Stack, Text, Tooltip } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconSettings } from "@tabler/icons-react";

interface SearchConfigProps {
  opened: boolean;
  /** The button toggles the popover. */
  onOpen: () => void;
  /** Close without applying: Cancel, Escape or the settings button. */
  onCancel: () => void;
  onApply: () => void;
  /** Why Apply is disabled, if it is (e.g. no vehicle picked). */
  applyBlockedReason?: string | null;
  /** A required setting is missing: marks the button. */
  attention?: boolean;
  /** The settings, rendered in the popover. */
  children: React.ReactNode;
}

/** The settings button of the search bar. Every search option lives in its
 * popover; changes take effect on Apply only. */
const SearchConfig: React.FC<SearchConfigProps> = ({
  opened,
  onOpen,
  onCancel,
  onApply,
  applyBlockedReason = null,
  attention = false,
  children,
}) => (
  <Popover
    opened={opened}
    onChange={(next) => !next && onCancel()}
    // Dropdowns inside (vehicle, module) render in a portal so the popover
    // cannot clip them; a click in them must not count as "outside".
    closeOnClickOutside={false}
    position="bottom-start"
    withArrow
    shadow="md"
    width={400}
  >
    <Popover.Target>
      <Tooltip label={attention ? "Search settings: a setting is required" : "Search settings"} disabled={opened}>
        <Indicator color="red" size={10} offset={4} disabled={!attention} processing>
          <ActionIcon
            variant={opened ? "light" : "default"}
            size={42}
            radius="sm"
            aria-label="Search settings"
            aria-expanded={opened}
            onClick={() => (opened ? onCancel() : onOpen())}
          >
            <IconSettings size={18} />
          </ActionIcon>
        </Indicator>
      </Tooltip>
    </Popover.Target>
    <Popover.Dropdown p="md">
      <Stack gap="md">{children}</Stack>
      <Divider my="sm" />
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text size="xs" c="red">
          {applyBlockedReason}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Button variant="default" size="xs" onClick={onCancel}>
            Cancel
          </Button>
          <Button color="brand" size="xs" onClick={onApply} disabled={applyBlockedReason !== null}>
            Apply
          </Button>
        </Group>
      </Group>
    </Popover.Dropdown>
  </Popover>
);

export default SearchConfig;
